import { useEffect, useMemo } from 'react';
import L from 'leaflet';
import { ScanLine } from 'lucide-react';
import { planCheck, PLAN_TOLS } from '../lib/planCheck';
import { flyTo } from '../lib/vis';

const m = (v) => `${v.toFixed(2).replace('.', ',')} m`;

// red overlay of pipe sections deviating from the CAD plan
export function usePlanCheckLayer(mapRef, pipes, fittings, cad) {
  const res = useMemo(() => (cad?.check ? planCheck({ pipes, fittings, cad }) : []), [pipes, fittings, cad]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !res.length) return undefined;
    if (!map.getPane('cadDev')) { map.createPane('cadDev').style.zIndex = 470; map.getPane('cadDev').style.pointerEvents = 'none'; }
    const g = L.layerGroup(res.flatMap((r) => r.runs.map((ln) => L.polyline(ln, { pane: 'cadDev', color: '#dc2626', weight: 9, opacity: 0.55, className: 'plan-dev-line', interactive: false })))).addTo(map);
    return () => g.remove();
  }, [mapRef, res]);
}

export default function PlanCheck({ t, project, updateProject }) {
  const cad = project.cad, tol = cad.tol ?? 1;
  const res = useMemo(() => (cad.check ? planCheck(project) : []), [cad.check, project]);
  const set = (p) => updateProject((pr) => ({ ...pr, cad: { ...pr.cad, ...p } }));
  return (
    <div className="plan-check" data-testid="plan-check">
      <div className="row">
        <b><ScanLine size={14} /> {t.pcTitle}</b>
        <select data-testid="plan-check-tol" value={tol} onChange={(e) => set({ tol: +e.target.value })}>{PLAN_TOLS.map((v) => <option key={v} value={v}>{`± ${String(v).replace('.', ',')} m`}</option>)}</select>
        <label className="pc-on"><input type="checkbox" data-testid="plan-check-toggle" checked={!!cad.check} onChange={(e) => set({ check: e.target.checked })} /> {t.pcShow}</label>
      </div>
      {cad.check && !res.length && <small className="muted" data-testid="plan-check-empty">{t.pcNone}</small>}
      {res.length > 0 && (
        <>
          <small data-testid="plan-check-summary">{res.filter((r) => !r.ok).length} / {res.length} {t.pcBad}</small>
          <div className="pc-list">
            {res.map((r) => (
              <button key={r.id} type="button" className={`pc-row ${r.ok ? 'ok' : 'bad'}`} data-testid={`plan-check-row-${r.id}`} onClick={() => flyTo(r.runs.flat().length ? r.runs.flat() : r.pts)}>
                <b>{r.name}</b><span>{r.dn ? `DN ${r.dn} · ` : ''}{m(r.len)}</span><span>max {m(r.max)}</span><span>{r.ok ? `✓ ${t.pcOk}` : `${t.pcDev} ${m(r.devLen)}`}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
